import { createFileRoute } from "@tanstack/react-router";
import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { apiList } from "@/integrations/api/client";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ReportShell } from "@/features/reports/ReportShell";
import { KpiCard } from "@/features/reports/KpiCard";
import { Loader2, AlertTriangle } from "lucide-react";
import type { ReportFilters } from "@/features/reports/types";
import { useBranch } from "@/context/BranchContext";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { isBranchLocked } from "@/lib/roles";

export const Route = createFileRoute("/_authenticated/reports/downtime")({
  component: DowntimeReport,
});


const CLOSED_STATUSES = new Set(["Resolved", "Verified & Closed", "Closed"]);

type Row = {
  id: string;
  wo_number: string;
  status: string;
  downtime_minutes: number | null;
  reported_at: string;
  resolved_at: string | null;
  closed_at: string | null;
  branch_id: string | null;
  department_id: string | null;
  reporting_department_id: string | null;
  asset: { asset_code: string; name: string; branch_id: string | null; custodian_dept_id: string | null; custodian_dept: { name: string } | null } | null;
  reporting_department: { name: string } | null;
  department: { name: string } | null;
};

type ComputedRow = Row & { computedMin: number; isLive: boolean };

function DowntimeReport() {
  const [filters, setFilters] = useState<ReportFilters>({});
  const { currentBranchId } = useBranch();
  const { data: me } = useCurrentUser();
  const lockedBranchId = isBranchLocked(me?.roles) ? (me?.profile?.branch_id ?? null) : null;
  const effectiveBranchId = lockedBranchId ?? filters.branchId ?? currentBranchId ?? null;
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["rep_downtime", { ...filters, branchId: effectiveBranchId }],
    queryFn: async () => {
      const params: Record<string, any> = { limit: 2000, type: "Breakdown" };
      if (effectiveBranchId) params.branch_id = effectiveBranchId;
      if (filters.departmentId) params.department = filters.departmentId;
      if (filters.dateFrom) params.reported_at__gte = filters.dateFrom;
      if (filters.dateTo) params.reported_at__lte = filters.dateTo;
      
      const res = await apiList<any>("/api/v1/work-orders/", params);
      const list = res.results || [];

      const now = Date.now();
      const computed: ComputedRow[] = [];
      const mttrParts: number[] = [];
      for (const r of list) {
        let mins = 0;
        let isLive = false;
        if (r.downtime_minutes != null && r.downtime_minutes > 0) {
          mins = r.downtime_minutes;
        } else if (r.reported_at) {
          const start = new Date(r.reported_at).getTime();
          const end = r.resolved_at
            ? new Date(r.resolved_at).getTime()
            : r.closed_at
            ? new Date(r.closed_at).getTime()
            : null;
          if (end != null) {
            mins = Math.max(0, Math.round((end - start) / 60000));
          } else if (!CLOSED_STATUSES.has(r.status)) {
            mins = Math.max(0, Math.round((now - start) / 60000));
            isLive = true;
          }
        }
        if (r.resolved_at && r.reported_at && CLOSED_STATUSES.has(r.status)) {
          mttrParts.push(
            Math.max(0, Math.round((new Date(r.resolved_at).getTime() - new Date(r.reported_at).getTime()) / 60000))
          );
        }
        if (mins > 0) computed.push({ ...r, computedMin: mins, isLive });
      }
      computed.sort((a, b) => b.computedMin - a.computedMin);
      const totalMin = computed.reduce((a, b) => a + b.computedMin, 0);
      const mttr = mttrParts.length ? Math.round(mttrParts.reduce((a, b) => a + b, 0) / mttrParts.length) : null;
      return { rows: computed, totalHours: Math.round(totalMin / 6) / 10, mttr };
    },
  });

  const rows = data?.rows ?? [];
  const deptName = (r: Row) =>
    r.reporting_department?.name ?? r.department?.name ?? r.asset?.custodian_dept?.name ?? "—";

  const exportRows = useMemo(
    () => () =>
      rows.map((r) => ({
        WO: r.wo_number,
        Asset: r.asset?.name ?? "",
        Code: r.asset?.asset_code ?? "",
        Department: deptName(r),
        DowntimeMin: r.computedMin,
        Hours: (r.computedMin / 60).toFixed(1),
        Reported: r.reported_at,
        Resolved: r.resolved_at ?? "",
        Status: r.status,
        Live: r.isLive ? "yes" : "",
      })),
    [rows]
  );

  return (
    <ReportShell
      id="R-05"
      title="Downtime Report"
      description="Hours lost per asset and department."
      filters={filters}
      onChange={setFilters}
      enabled={["branchId", "departmentId", "dateFrom", "dateTo"]}
      exportRows={exportRows}
    >
      <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
        <KpiCard label="Total Downtime" value={isLoading ? "—" : `${data?.totalHours ?? 0} h`} />
        <KpiCard label="Affected Work Orders" value={isLoading ? "—" : rows.length} />
        <KpiCard label="MTTR (min)" value={isLoading ? "—" : data?.mttr ?? "—"} />
      </div>
      {error ? (
        <Card>
          <CardContent className="flex items-start gap-3 p-4 text-sm text-destructive">
            <AlertTriangle className="h-4 w-4 mt-0.5" />
            <div className="flex-1">
              <div className="font-medium">Failed to load downtime data</div>
              <div className="text-xs opacity-80">{(error as Error).message}</div>
            </div>
            <Button size="sm" variant="outline" onClick={() => refetch()}>Retry</Button>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            {isLoading ? (
              <div className="grid place-items-center py-10">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>WO #</TableHead>
                    <TableHead>Asset</TableHead>
                    <TableHead>Department</TableHead>
                    <TableHead className="text-right">Downtime (min)</TableHead>
                    <TableHead className="text-right">Hours</TableHead>
                    <TableHead>Reported At</TableHead>
                    <TableHead>Resolved At</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.slice(0, 200).map((r) => (
                    <TableRow key={r.id}>
                      <TableCell className="font-mono text-xs">{r.wo_number}</TableCell>
                      <TableCell>{r.asset?.name ?? "—"}</TableCell>
                      <TableCell>{deptName(r)}</TableCell>
                      <TableCell className="text-right tabular-nums">
                        {r.computedMin}
                        {r.isLive && <Badge variant="outline" className="ml-2 text-[10px]">live</Badge>}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">{(r.computedMin / 60).toFixed(1)}</TableCell>
                      <TableCell className="text-sm">{r.reported_at.slice(0, 16).replace("T", " ")}</TableCell>
                      <TableCell className="text-sm">
                        {r.resolved_at ? r.resolved_at.slice(0, 16).replace("T", " ") : "—"}
                      </TableCell>
                      <TableCell className="text-sm">{r.status}</TableCell>
                    </TableRow>
                  ))}
                  {rows.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={8} className="py-8 text-center text-sm text-muted-foreground">
                        No downtime recorded.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      )}
    </ReportShell>
  );
}
